import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Button, Container, Table, Spinner, Modal, Form, InputGroup, Alert, Row, Col, Card, Badge, Collapse } from 'react-bootstrap';
import { FaEdit, FaTrash, FaRedo, FaTable, FaTh } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import { getClients, createClient, updateClient, deleteClient, getSales } from '../services/api';
import { trackAction } from '../services/analytics';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Pagination from '../components/Pagination';
import { useDebounce } from '../hooks/useDebounce';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import AuthContext from '../context/AuthContext';

import PageSizeSelector from '../components/PageSizeSelector';
import { handlePhoneBlurLogic } from '../utils/phoneUtils';
import useIsMobile from '../hooks/useIsMobile';

const ReadOnlyInput = ({ value, ...rest }) => (
    <Form.Control {...rest} value={value} readOnly />
);

const ClientsPage = () => {
    const { user, business } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);

    const isSubscriptionActive = ['active', 'trialing'].includes(business?.subscription_status);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({ name: '', phone_country_code: '', phone: '', address: '' });
    const [editingClient, setEditingClient] = useState(null);

    // New state for viewing client details and sales
    const [viewingClient, setViewingClient] = useState(null);
    const [clientSales, setClientSales] = useState([]);
    const [loadingSales, setLoadingSales] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('clientsViewMode') || 'table');

    const toggleViewMode = (mode) => {
        setViewMode(mode);
        localStorage.setItem('clientsViewMode', mode);
    };

    const fetchClients = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getClients({ page: currentPage, limit: pageSize, name: debouncedSearchTerm });
            setClients(response.data.clients);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            toast.error(t('clientsPage.toasts.fetchFailed'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, debouncedSearchTerm, t]);

    useEffect(() => {
        if (hasPermission(PERMISSIONS.VIEW_CLIENTS)) {
            fetchClients();
        }
        else {
            setLoading(false);
            setError(t('clientsPage.toasts.permissionError'));
        }
    }, [fetchClients, hasPermission, t]);

    const handleModalClose = () => {
        setShowModal(false);
        setEditingClient(null);
        setModalData({ name: '', email: '', phone_country_code: '', phone: '', address: '' });
    };

    const handleCreateClick = () => {
        setEditingClient(null);
        setModalData({ name: '', email: '', phone_country_code: '', phone: '', address: '' });
        setShowModal(true);
    };

    const handleEditClick = (client) => {
        setEditingClient(client);
        setModalData(client);
        setShowModal(true);
    };

    const handleDelete = async (clientId) => {
        const result = await Swal.fire({
            title: t('areYouSure'),
            text: t('clientsPage.toasts.deleteConfirm'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ev-action-danger-bg)',
            cancelButtonColor: 'var(--ev-bg-subtle)',
            confirmButtonText: t('yesDelete'),
            cancelButtonText: t('cancel')
        });

        if (result.isConfirmed) {
            const toastId = toast.loading(t('clientsPage.toasts.deleting'));
            try {
                await deleteClient(clientId);
                fetchClients(); // Refetch clients
                trackAction('client_deleted');
                toast.success(t('clientsPage.toasts.deleted'), { id: toastId });
            } catch (error) {
                toast.error(t('clientsPage.toasts.deleteFailed'), { id: toastId });
            }
        }
    };

    const handleViewClick = async (client) => {
        setViewingClient(client);
        setLoadingSales(true);
        try {
            // Search by client_id AND also by name to catch sales created before client was linked
            const params = { pagination: 'false' };
            if (client.client_id) params.client_id = client.client_id;
            if (client.name) params.client = client.name;
            const response = await getSales(params);
            // Deduplicate by sale_id in case both filters match same sale
            const salesMap = new Map();
            (response.data || []).forEach(s => salesMap.set(s.sale_id, s));
            setClientSales(Array.from(salesMap.values()));
        } catch (error) {
            console.error("Error fetching sales:", error);
            toast.error(t('clientsPage.toasts.fetchSalesFailed'));
            setClientSales([]);
        } finally {
            setLoadingSales(false);
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        const toastId = toast.loading(editingClient ? t('clientsPage.toasts.updating') : t('clientsPage.toasts.creating'));
        try {
            if (editingClient) {
                await updateClient(editingClient.client_id, modalData);
                trackAction('client_updated'); // sin PII: nada de nombre/teléfono/email
                toast.success(t('clientsPage.toasts.updated'), { id: toastId });
            } else {
                await createClient(modalData);
                trackAction('client_created');
                toast.success(t('clientsPage.toasts.created'), { id: toastId });
            }
            fetchClients(); // Refetch clients
            handleModalClose();
        } catch (error) {
            trackAction('client_save_failed', { is_edit: !!editingClient });
            toast.error(editingClient ? t('clientsPage.toasts.updateFailed') : t('clientsPage.toasts.createFailed'), { id: toastId });
        }
    };

    const handleModalChange = (e) => {
        setModalData({ ...modalData, [e.target.name]: e.target.value });
    };

    const handleModalCountryCodeChange = (value) => {
        setModalData(prev => ({ ...prev, phone_country_code: value }));
    };

    const handlePhoneBlur = () => {
        handlePhoneBlurLogic(
            modalData.phone,
            modalData.phone_country_code,
            (newCountry) => setModalData(prev => ({ ...prev, phone_country_code: newCountry })),
            (newPhone) => setModalData(prev => ({ ...prev, phone: newPhone }))
        );
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    return (
        <Container className="mt-4">
            <div className="d-flex justify-content-between align-items-center">
                <h2>{t('clientsPage.title')}</h2>
                {hasPermission(PERMISSIONS.CREATE_CLIENTS) && (
                    <div title={!isSubscriptionActive ? t('subscriptionRequired') : ''} className="d-none d-md-block">
                        <Button
                            variant="primary"
                            onClick={handleCreateClick}
                            disabled={!isSubscriptionActive}
                        >
                            {t('clientsPage.newClientButton')}
                        </Button>
                    </div>
                )}
            </div>

            {hasPermission(PERMISSIONS.CREATE_CLIENTS) && (
                <div className="d-md-none position-fixed bottom-0 start-0 end-0 px-3 pt-3 bg-white shadow-lg" style={{ zIndex: 1000, borderTop: '1px solid #dee2e6', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
                    <Button
                        variant="primary"
                        onClick={handleCreateClick}
                        disabled={!isSubscriptionActive}
                        className="w-100"
                    >
                        {t('clientsPage.newClientButton')}
                    </Button>
                </div>
            )}
            <p>{t('clientsPage.subtitle')}</p>

            <div className="d-md-none mb-3">
                <Button variant="link" onClick={() => setShowFilters(!showFilters)} className="p-0 text-decoration-none">
                    {showFilters ? t('clientsPage.hideFilters', 'Ocultar filtros') : t('clientsPage.viewFilters', 'Ver filtros')}
                </Button>
            </div>

            <Collapse in={showFilters || window.innerWidth >= 768}>
                <div id="clients-filters">
                    <InputGroup className="mb-3">
                        <Form.Control
                            placeholder={t('clientsPage.searchPlaceholder')}
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </InputGroup>
                </div>
            </Collapse>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* View Mode Toggle */}
            <div className="d-flex justify-content-end align-items-center mb-3">
                <div
                    className="sales-view-toggle"
                    role="group"
                    aria-label={t('clientsPage.viewToggle.label', 'Cambiar vista')}
                >
                    <button
                        id="clients-view-table"
                        className={`sales-view-toggle__btn${viewMode === 'table' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('table')}
                        aria-pressed={viewMode === 'table'}
                        title={t('clientsPage.viewToggle.tableView', 'Vista tabla')}
                        aria-label={t('clientsPage.viewToggle.tableView', 'Vista tabla')}
                    >
                        <FaTable aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('clientsPage.viewToggle.tableView', 'Tabla')}</span>
                    </button>
                    <button
                        id="clients-view-cards"
                        className={`sales-view-toggle__btn${viewMode === 'cards' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('cards')}
                        aria-pressed={viewMode === 'cards'}
                        title={t('clientsPage.viewToggle.cardsView', 'Vista tarjetas')}
                        aria-label={t('clientsPage.viewToggle.cardsView', 'Vista tarjetas')}
                    >
                        <FaTh aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('clientsPage.viewToggle.cardsView', 'Tarjetas')}</span>
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center"><Spinner animation="border" /></div>
            ) : viewMode === 'table' ? (
                <div className="modern-table-wrapper mt-1">
                    <Table hover responsive className="modern-table">
                        <thead>
                            <tr>
                                <th>{t('clientsPage.table.name')}</th>
                                <th>{t('clientsPage.table.email')}</th>
                                <th>{t('clientsPage.table.phone')}</th>
                                <th>{t('clientsPage.table.address')}</th>
                                <th>{t('clientsPage.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.length > 0 ? clients.map(client => (
                                <tr key={client.client_id}>
                                    <td>{client.name}</td>
                                    <td>{client.email}</td>
                                    <td>{`${client.phone_country_code || ''} ${client.phone || ''}`}</td>
                                    <td>{client.address}</td>
                                    <td className="text-center">
                                        <div className="btn-actions">
                                            <Button variant="info" size="sm" onClick={() => handleViewClick(client)} title={t('clientsPage.viewButton')}>
                                                <i className="bi bi-eye"></i>
                                            </Button>
                                            {hasPermission(PERMISSIONS.EDIT_CLIENTS) && (
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => handleEditClick(client)}
                                                    title={t('clientsPage.editButton')}
                                                    aria-label={t('clientsPage.editButton')}
                                                >
                                                    <FaEdit />
                                                </Button>
                                            )}
                                            {hasPermission(PERMISSIONS.DELETE_CLIENTS) && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(client.client_id)}
                                                    title={t('clientsPage.deleteButton')}
                                                    aria-label={t('clientsPage.deleteButton')}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="5" className="text-center py-4 text-muted">{t('clientsPage.noClients')}</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            ) : (
                /* ── Cards view ── */
                <div>
                    {clients.length === 0 ? (
                        <div className="text-center py-5 text-muted">{t('clientsPage.noClients')}</div>
                    ) : (
                        <div className="sales-cards-grid">
                            {clients.map(client => (
                                <div key={client.client_id} className="contact-card">
                                    <div className="contact-card__header">
                                        <span className="contact-card__name" title={client.name}>{client.name}</span>
                                    </div>
                                    <div className="contact-card__info">
                                        {client.email && (
                                            <div className="contact-card__info-row">
                                                <i className="bi bi-envelope contact-card__info-icon"></i>
                                                <span className="contact-card__info-value">{client.email}</span>
                                            </div>
                                        )}
                                        {(client.phone || client.phone_country_code) && (
                                            <div className="contact-card__info-row">
                                                <i className="bi bi-telephone contact-card__info-icon"></i>
                                                <span className="contact-card__info-value">{`${client.phone_country_code || ''} ${client.phone || ''}`.trim()}</span>
                                            </div>
                                        )}
                                        {client.address && (
                                            <div className="contact-card__info-row">
                                                <i className="bi bi-geo-alt contact-card__info-icon"></i>
                                                <span className="contact-card__info-value">{client.address}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="contact-card__actions btn-actions">
                                        <Button variant="info" size="sm" onClick={() => handleViewClick(client)} title={t('clientsPage.viewButton')}>
                                            <i className="bi bi-eye"></i>
                                        </Button>
                                        {hasPermission(PERMISSIONS.EDIT_CLIENTS) && (
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleEditClick(client)}
                                                title={t('clientsPage.editButton')}
                                                aria-label={t('clientsPage.editButton')}
                                            >
                                                <FaEdit />
                                            </Button>
                                        )}
                                        {hasPermission(PERMISSIONS.DELETE_CLIENTS) && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDelete(client.client_id)}
                                                title={t('clientsPage.deleteButton')}
                                                aria-label={t('clientsPage.deleteButton')}
                                            >
                                                <FaTrash />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                <PageSizeSelector pageSize={pageSize} onPageSizeChange={handlePageSizeChange} />
            </div>

            <Modal show={showModal} onHide={handleModalClose} backdrop={isMobile ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingClient ? t('clientsPage.modal.editTitle') : t('clientsPage.modal.createTitle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleModalSubmit}>
                    <fieldset disabled={editingClient && !hasPermission(PERMISSIONS.EDIT_CLIENTS)}>
                        <Modal.Body>
                            <React.Fragment>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('clientsPage.modal.nameLabel')}</Form.Label>
                                    <Form.Control type="text" name="name" value={modalData.name} onChange={handleModalChange} required />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('clientsPage.modal.emailLabel')}</Form.Label>
                                    <Form.Control type="email" name="email" value={modalData.email || ''} onChange={handleModalChange} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('clientsPage.modal.phoneLabel')}</Form.Label>
                                    <Row>
                                        <Col xs={5} md={4}>
                                            <PhoneInput
                                                value={modalData.phone_country_code}
                                                onChange={handleModalCountryCodeChange}
                                                numberInputComponent={ReadOnlyInput}
                                                className="phone-input-custom"
                                                defaultCountry="CO"
                                                international
                                            />                                        </Col>
                                        <Col xs={7} md={8}>
                                            <Form.Control
                                                type="tel"
                                                name="phone"
                                                value={modalData.phone || ''}
                                                onChange={handleModalChange}
                                                onBlur={handlePhoneBlur}
                                            />
                                        </Col>
                                    </Row>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('clientsPage.modal.addressLabel')}</Form.Label>
                                    <Form.Control as="textarea" rows={3} name="address" value={modalData.address || ''} onChange={handleModalChange} />
                                </Form.Group>
                            </React.Fragment>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleModalClose}>{t('clientsPage.modal.closeButton')}</Button>
                            {((editingClient && hasPermission(PERMISSIONS.EDIT_CLIENTS)) || (!editingClient && hasPermission(PERMISSIONS.CREATE_CLIENTS))) &&
                                <Button variant="primary" type="submit">{t('clientsPage.modal.saveButton')}</Button>
                            }
                        </Modal.Footer>
                    </fieldset>
                </Form>
            </Modal>

            {/* Client Details & Sales History Modal */}
            <Modal show={!!viewingClient} onHide={() => setViewingClient(null)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>{viewingClient?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <h5>{t('clientsPage.detailsTitle')}</h5>
                            <p><strong>{t('clientsPage.table.email')}:</strong> {viewingClient?.email || t('common.notAvailable')}</p>
                            <p><strong>{t('clientsPage.table.phone')}:</strong> {`${viewingClient?.phone_country_code || ''} ${viewingClient?.phone || ''}`}</p>
                            <p><strong>{t('clientsPage.table.address')}:</strong> {viewingClient?.address || t('common.notAvailable')}</p>
                        </Col>
                        <Col md={6}>
                            <h5>{t('clientsPage.salesHistoryTitle')}</h5>
                            {loadingSales ? (
                                <div className="text-center"><Spinner animation="border" size="sm" /></div>
                            ) : clientSales.length > 0 ? (
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {clientSales.map(sale => (
                                        <Card key={sale.sale_id} className="mb-3 shadow-sm border-0">
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        <h6 className="mb-1 fw-bold text-primary">
                                                            {t('clientsPage.salesTable.invoice')} #{sale.invoice_number}
                                                            {sale.is_recurring_template && <Badge bg="info" className="ms-2" pill>{t('clientsPage.recurringBadge', 'Recurrente')}</Badge>}
                                                        </h6>
                                                        <small className="text-muted">
                                                            <i className="bi bi-calendar-event me-1"></i>
                                                            {new Date(sale.created_at).toLocaleDateString()}
                                                        </small>
                                                    </div>
                                                    <Badge bg={sale.status === 'COMPLETED' ? 'success' : 'warning'} pill>
                                                        {sale.status}
                                                    </Badge>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                                                    <span className="text-muted small">{t('clientsPage.salesTable.total')}</span>
                                                    <span className="fw-bold fs-5 text-dark">
                                                        ${parseFloat(sale.total_amount).toLocaleString()}
                                                    </span>
                                                </div>
                                                <div className="mt-2 text-end">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => navigate('/sales', { state: { repeatSale: sale } })}
                                                    >
                                                        <FaRedo className="me-1" />{t('clientsPage.repeatSale', 'Repetir venta')}
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <p>{t('clientsPage.noSalesFound')}</p>
                            )}
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setViewingClient(null)}>{t('clientsPage.modal.closeButton')}</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ClientsPage;